import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { StandardURL } from '../services/constants';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&display=swap');

:root {
  --bg: #0a0a0a;
  --surface: #111111;
  --surface2: #181818;
  --border: #2a2a2a;
  --accent: #e8ff3c;
  --accent-dim: rgba(232, 255, 60, 0.10);
  --text: #f0f0f0;
  --muted: #777777;
  --red: #ff4545;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.auth-page {
  min-height: 100vh;
  width: 100%;
  background: var(--bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'DM Sans', sans-serif;
  padding: 2rem 1rem;
  position: relative;
  overflow: hidden;
}

/* Background grid */
.auth-page::before {
  content: '';
  position: absolute;
  inset: 0;

  background-image:
    linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px);

  background-size: 48px 48px;
  pointer-events: none;
}

/* Subtle glow */
.auth-page::after {
  content: '';
  position: absolute;
  width: 500px;
  height: 500px;
  background: var(--accent);
  opacity: 0.025;
  filter: blur(120px);
  border-radius: 50%;
  pointer-events: none;
}

.auth-card {
  position: relative;
  z-index: 1;

  width: 100%;
  max-width: 420px;

  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;

  padding: 2.8rem 2.5rem;

  box-shadow:
    0 25px 80px rgba(0, 0, 0, 0.45),
    0 0 60px rgba(232, 255, 60, 0.025);
}

/* =========================
   BACK LINK
========================= */

.auth-back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;

  color: var(--muted);
  font-size: 0.78rem;
  text-decoration: none;

  margin-bottom: 1.4rem;

  transition: color 0.2s ease;
}

.auth-back-link:hover {
  color: var(--accent);
}

/* =========================
   LOGO
========================= */

.auth-logo {
  display: block;

  font-family: 'Bebas Neue', sans-serif;
  font-size: 2.4rem;
  line-height: 1;

  letter-spacing: 0.12em;

  color: var(--accent);
  text-align: center;

  margin-bottom: 0.55rem;
}

/* =========================
   TITLE
========================= */

.auth-title {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.7rem;
  line-height: 1;

  letter-spacing: 0.07em;

  color: var(--text);
  text-align: center;

  margin-bottom: 0.5rem;
}

/* =========================
   SUBTITLE
========================= */

.auth-subtitle {
  text-align: center;

  color: var(--muted);

  font-size: 0.82rem;
  line-height: 1.5;

  margin-bottom: 0.35rem;
}

.auth-subtitle:last-of-type {
  margin-bottom: 1.5rem;
}

/* =========================
   DIVIDER
========================= */

.auth-divider {
  width: 100%;
  height: 1px;

  background: var(--border);

  margin: 1.5rem 0;
}

/* =========================
   FORM
========================= */

.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;

  margin-bottom: 1.35rem;
}

.field-wrap {
  width: 100%;
}

/* =========================
   INPUTS
========================= */

.auth-input {
  width: 100%;
  height: 48px;

  background: var(--surface2);

  border: 1px solid var(--border);
  border-radius: 6px;

  color: var(--text);

  font-family: 'DM Sans', sans-serif;
  font-size: 0.88rem;

  padding: 0 1rem;

  outline: none;

  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;
}

.auth-input:hover {
  border-color: #3a3a3a;
}

.auth-input:focus {
  background: #1b1b1b;

  border-color: var(--accent);

  box-shadow:
    0 0 0 3px var(--accent-dim);
}

.auth-input::placeholder {
  color: #5f5f5f;
}

/* =========================
   REMEMBER / FORGOT
========================= */

.auth-row {
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-bottom: 1.4rem;

  gap: 1rem;
}

.auth-check-label {
  display: flex;
  align-items: center;

  gap: 0.5rem;

  color: var(--muted);

  font-size: 0.78rem;

  cursor: pointer;
  user-select: none;
}

.auth-check-label input[type="checkbox"] {
  appearance: none;

  width: 15px;
  height: 15px;

  border: 1px solid var(--border);
  border-radius: 3px;

  background: var(--surface2);

  cursor: pointer;

  position: relative;

  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.auth-check-label input[type="checkbox"]:checked {
  background: var(--accent);
  border-color: var(--accent);
}

.auth-check-label input[type="checkbox"]:checked::after {
  content: '';

  position: absolute;

  width: 4px;
  height: 8px;

  border-right: 2px solid #000;
  border-bottom: 2px solid #000;

  transform: rotate(45deg);

  left: 4px;
  top: 1px;
}

/* =========================
   FORGOT PASSWORD
========================= */

.auth-link {
  font-family: 'DM Sans', sans-serif;

  font-size: 0.78rem;
  font-weight: 500;

  color: var(--accent);

  text-decoration: none;

  background: none;
  border: none;

  cursor: pointer;

  padding: 0;

  transition: opacity 0.2s ease;
}

.auth-link:hover {
  opacity: 0.7;
  text-decoration: underline;
}

/* =========================
   BUTTON
========================= */

.auth-btn {
  width: 100%;
  min-height: 48px;

  background: var(--accent);
  color: #000;

  border: 1px solid var(--accent);
  border-radius: 6px;

  padding: 0.78rem 1.5rem;

  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.1rem;

  letter-spacing: 0.08em;

  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 0.5rem;

  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    opacity 0.2s ease,
    transform 0.15s ease,
    box-shadow 0.2s ease;
}

.auth-btn:hover:not(:disabled) {
  background: #f0ff61;
  border-color: #f0ff61;

  transform: translateY(-1px);

  box-shadow:
    0 8px 25px rgba(232, 255, 60, 0.12);
}

.auth-btn:active:not(:disabled) {
  transform: translateY(0);
}

.auth-btn:disabled {
  background: var(--surface2);
  border-color: var(--border);
  color: var(--muted);

  cursor: not-allowed;

  transform: none;
  box-shadow: none;
}

/* =========================
   FOOTER
========================= */

.auth-footer-text {
  text-align: center;

  margin-top: 1.5rem;

  font-size: 0.8rem;

  color: var(--muted);
}

.auth-footer-text a {
  color: var(--accent);

  text-decoration: none;

  font-weight: 500;
}

.auth-footer-text a:hover {
  text-decoration: underline;
}

/* =========================
   MOBILE
========================= */

@media (max-width: 480px) {
  .auth-page {
    padding: 1rem;
  }

  .auth-card {
    padding: 2.2rem 1.4rem;
    border-radius: 10px;
  }

  .auth-logo {
    font-size: 2.1rem;
  }

  .auth-title {
    font-size: 1.5rem;
  }

  .auth-row {
    align-items: flex-start;
  }
}
`;

const Login = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    setIsLoading(true);
    e.preventDefault();
    try {
      const response = await axios.post(
        `${StandardURL}/iMusic/auth/login`,
        formData
      );
      localStorage.setItem("token", response.data.response);
      toast.success("Please check your email for OTP!");
      setIsLoading(false);
      navigate("/OtpVerification");
    } catch (error) {
      console.error(error);
      if (error.response && error.response.status === 401) {
        toast.error("Unauthorized access");
      } else if (error.response && error.response.status === 406) {
        toast.error("Please enter the correct email and password");
      } else if (error.response && error.response.status === 400) {
        toast.error("Your password is expired please reset password.");
      } else {
        toast.error("An error occurred, please try again later");
      }
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="auth-page">
        <div className="auth-card">
          <a href="/" className="auth-back-link">&larr; Back</a>

          <span className="auth-logo">MUSICA</span>
          <h2 className="auth-title">Welcome Back</h2>
          <p className="auth-subtitle">Sign in to your account to continue</p>
          <div className="auth-divider" />

          <form onSubmit={handleSubmit}>
            <div className="field-group">
              <div className="field-wrap">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="auth-input"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div className="field-wrap">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="auth-input"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="auth-row">
              <label className="auth-check-label">
                <input id="remember-me" name="remember-me" type="checkbox" />
                Remember me
              </label>
              <a href="/forgot-password" className="auth-link">Forgot password?</a>
            </div>

            <button type="submit" className="auth-btn" disabled={isLoading}>
              {isLoading ? (
                <Oval height={20} width={20} color="#000" visible={true} ariaLabel="loading-indicator" secondaryColor="#000" />
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <p className="auth-footer-text">
            Don't have an account?{" "}
            <a href="/Registration">Create one</a>
          </p>
        </div>
      </div>
    </>
  );
};

export default Login;